import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Autocomplete,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Add as AddIcon,
  Calculate as CalculateIcon,
  PhoneAndroid as RetriggerIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useLoanApplications,
  useApproveApplication,
  useRejectApplication,
  useLoanProducts,
  useCalculateLoan,
  useSubmitApplication,
  useRetriggerFeePayment,
} from '../../hooks/loans/useLoans';
import { useCustomers, useCustomerLoanLimit } from '../../hooks/customers/useCustomers';
import { LoanApplication, ApplicationStatus, LoanProduct, LoanCalculationResponse } from '../../api/types/loan.types';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/auth/useAuth';

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | undefined>(undefined);
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [cooldownMap, setCooldownMap] = useState<Record<string, boolean>>({});

  // New application form state
  const [newApplicationOpen, setNewApplicationOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [requestedAmount, setRequestedAmount] = useState<string>('');
  const [tenorWeeks, setTenorWeeks] = useState<1 | 2 | 4>(4);
  const [loanPurpose, setLoanPurpose] = useState('');
  const [calculation, setCalculation] = useState<LoanCalculationResponse | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');

  const { data, isLoading, error } = useLoanApplications({ status: statusFilter });
  const { data: customersData } = useCustomers();
  const { data: products } = useLoanProducts();
  const { data: customerLoanLimit } = useCustomerLoanLimit(selectedCustomerId);
  const approveMutation = useApproveApplication();
  const rejectMutation = useRejectApplication();
  const calculateMutation = useCalculateLoan();
  const submitMutation = useSubmitApplication();
  const retriggerMutation = useRetriggerFeePayment();

  // Get selected customer and product details
  const selectedCustomer = customersData?.customers?.find((c) => c.customerId === selectedCustomerId);
  const selectedProduct = products?.find((p: LoanProduct) => p.productId === selectedProductId);

  // Check for ?action=new parameter
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setNewApplicationOpen(true);
    }
  }, [searchParams]);

  const handleCloseNewApplication = () => {
    setNewApplicationOpen(false);
    navigate('/loans/applications', { replace: true });
    setSelectedCustomerId('');
    setSelectedProductId('');
    setRequestedAmount('');
    setTenorWeeks(4);
    setLoanPurpose('');
    setCalculation(null);
    setCustomerSearch('');
  };

  const handleCalculate = async () => {
    if (!selectedProductId || !requestedAmount) {
      enqueueSnackbar('Please select product and enter amount', { variant: 'warning' });
      return;
    }

    try {
      const result = await calculateMutation.mutateAsync({
        customerId: selectedCustomerId || undefined,
        productId: selectedProductId,
        requestedAmount: parseFloat(requestedAmount),
        tenorWeeks,
      });
      setCalculation(result);
      enqueueSnackbar('Loan calculated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Calculation failed',
        { variant: 'error' }
      );
    }
  };

  const handleSubmitNewApplication = async () => {
    if (!selectedCustomerId || !selectedProductId || !requestedAmount || !loanPurpose) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    try {
      const result = await submitMutation.mutateAsync({
        customerId: selectedCustomerId,
        productId: selectedProductId,
        requestedAmount: parseFloat(requestedAmount),
        tenorWeeks,
        loanPurpose,
      });

      if (result.feePaymentRequired) {
        enqueueSnackbar(
          `Application submitted. Customer must pay the processing fee of KES ${result.processingFee} via M-Pesa to proceed.`,
          { variant: 'info', autoHideDuration: 6000 }
        );
      } else {
        enqueueSnackbar('Application submitted successfully!', { variant: 'success' });
      }
      handleCloseNewApplication();
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Submission failed',
        { variant: 'error' }
      );
    }
  };

  const handleApproveClick = (application: LoanApplication) => {
    setSelectedApplication(application);
    setApproveDialogOpen(true);
  };

  const handleRejectClick = (application: LoanApplication) => {
    setSelectedApplication(application);
    setRejectDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedApplication || !user) return;

    try {
      await approveMutation.mutateAsync({
        applicationId: selectedApplication.applicationId,
        data: {
          approvedAmount: parseFloat(selectedApplication.approvedAmount ?? selectedApplication.requestedAmount),
          approvedBy: user.userId,
          notes: approvalNotes,
        },
      });
      enqueueSnackbar('Application approved successfully!', { variant: 'success' });
      setApproveDialogOpen(false);
      setApprovalNotes('');
      setSelectedApplication(null);
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Approval failed',
        { variant: 'error' }
      );
    }
  };

  const handleRejectConfirm = async () => {
    if (!selectedApplication || !user) return;

    try {
      await rejectMutation.mutateAsync({
        applicationId: selectedApplication.applicationId,
        data: {
          rejectedBy: user.userId,
          rejectionReason,
        },
      });
      enqueueSnackbar('Application rejected', { variant: 'info' });
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedApplication(null);
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Rejection failed',
        { variant: 'error' }
      );
    }
  };

  const handleRetriggerFee = async (app: LoanApplication) => {
    if (cooldownMap[app.applicationId]) return;

    try {
      await retriggerMutation.mutateAsync(app.applicationId);
      enqueueSnackbar('M-Pesa payment prompt re-sent to customer.', { variant: 'success' });
      // Start 60-second cooldown for this row
      setCooldownMap((prev) => ({ ...prev, [app.applicationId]: true }));
      setTimeout(() => {
        setCooldownMap((prev) => {
          const next = { ...prev };
          delete next[app.applicationId];
          return next;
        });
      }, 60_000);
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to resend payment prompt',
        { variant: 'error' }
      );
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(numAmount);
  };

  const getStatusChipProps = (status: ApplicationStatus) => {
    switch (status) {
      case 'APPROVED':
        return { label: 'Approved', color: 'success' as const };
      case 'PENDING':
        return { label: 'Under Review', color: 'warning' as const };
      case 'PENDING_FEE_PAYMENT':
        return { label: 'Awaiting Fee', color: 'default' as const };
      case 'REJECTED':
      case 'CANCELLED':
        return { label: status === 'REJECTED' ? 'Rejected' : 'Cancelled', color: 'error' as const };
      case 'DISBURSED':
        return { label: 'Disbursed', color: 'info' as const };
      case 'EXPIRED':
        return { label: 'Expired', color: 'default' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const isFeeExpired = (app: LoanApplication) => {
    if (!app.feeExpiresAt) return false;
    return new Date(app.feeExpiresAt) <= new Date();
  };

  const getAppStatus = (app: LoanApplication): ApplicationStatus =>
    app.applicationStatus;

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Loan Applications
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load applications. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Loan Applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and manage loan applications
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewApplicationOpen(true)}
        >
          New Application
        </Button>
      </Box>

      {/* Status Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label="All"
          onClick={() => setStatusFilter(undefined)}
          color={!statusFilter ? 'primary' : 'default'}
          variant={!statusFilter ? 'filled' : 'outlined'}
        />
        <Chip
          label="Awaiting Fee"
          onClick={() => setStatusFilter('PENDING_FEE_PAYMENT' as ApplicationStatus)}
          sx={
            statusFilter === 'PENDING_FEE_PAYMENT'
              ? { bgcolor: 'warning.light', color: 'warning.contrastText' }
              : { borderColor: 'warning.main', color: 'warning.dark' }
          }
          variant={statusFilter === 'PENDING_FEE_PAYMENT' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Pending"
          onClick={() => setStatusFilter('PENDING' as ApplicationStatus)}
          color={statusFilter === 'PENDING' ? 'warning' : 'default'}
          variant={statusFilter === 'PENDING' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Approved"
          onClick={() => setStatusFilter('APPROVED' as ApplicationStatus)}
          color={statusFilter === 'APPROVED' ? 'success' : 'default'}
          variant={statusFilter === 'APPROVED' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Disbursed"
          onClick={() => setStatusFilter('DISBURSED' as ApplicationStatus)}
          color={statusFilter === 'DISBURSED' ? 'info' : 'default'}
          variant={statusFilter === 'DISBURSED' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Rejected"
          onClick={() => setStatusFilter('REJECTED' as ApplicationStatus)}
          color={statusFilter === 'REJECTED' ? 'error' : 'default'}
          variant={statusFilter === 'REJECTED' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Expired"
          onClick={() => setStatusFilter('EXPIRED' as ApplicationStatus)}
          color={statusFilter === 'EXPIRED' ? 'default' : 'default'}
          variant={statusFilter === 'EXPIRED' ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Customer Name</strong></TableCell>
              <TableCell><strong>Requested Amount</strong></TableCell>
              <TableCell><strong>Tenor</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Applied Date</strong></TableCell>
              <TableCell><strong>Purpose</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data?.applications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No applications found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.applications.map((app) => {
                const appStatus = getAppStatus(app);
                const { label, color } = getStatusChipProps(appStatus);
                const isPendingFee = appStatus === 'PENDING_FEE_PAYMENT';
                const canApprove = appStatus === 'PENDING';
                const canRetrigger = isPendingFee && !isFeeExpired(app);

                return (
                  <TableRow
                    key={app.applicationId}
                    hover
                    sx={isPendingFee ? { bgcolor: 'warning.50' } : undefined}
                  >
                    <TableCell>{app.customerName}</TableCell>
                    <TableCell>{formatCurrency(parseFloat(app.requestedAmount))}</TableCell>
                    <TableCell>{app.tenorWeeks} week(s)</TableCell>
                    <TableCell>
                      <Chip
                        label={label}
                        size="small"
                        color={color}
                        sx={
                          isPendingFee
                            ? { bgcolor: 'orange', color: '#fff', fontWeight: 'bold' }
                            : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {app.dateApplied ? format(new Date(app.dateApplied), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell>{app.purpose}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {canRetrigger && (
                          <Tooltip
                            title={
                              cooldownMap[app.applicationId]
                                ? 'Please wait before resending'
                                : 'Resend M-Pesa fee prompt to customer'
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<RetriggerIcon />}
                                onClick={() => handleRetriggerFee(app)}
                                disabled={cooldownMap[app.applicationId] || retriggerMutation.isPending}
                              >
                                Resend Prompt
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {canApprove && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => handleApproveClick(app)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<RejectIcon />}
                              onClick={() => handleRejectClick(app)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Loan Application</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Customer: <strong>{selectedApplication?.customerName}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Amount: <strong>{selectedApplication && formatCurrency(parseFloat(selectedApplication.requestedAmount))}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Tenor: <strong>{selectedApplication?.tenorWeeks} week(s)</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Purpose: <strong>{selectedApplication?.purpose}</strong>
            </Typography>

            <TextField
              label="Approval Notes (optional)"
              multiline
              rows={3}
              fullWidth
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Loan Application</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Customer: <strong>{selectedApplication?.customerName}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Amount: <strong>{selectedApplication && formatCurrency(parseFloat(selectedApplication.requestedAmount))}</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Purpose: <strong>{selectedApplication?.purpose}</strong>
            </Typography>

            <TextField
              label="Rejection Reason"
              multiline
              rows={3}
              fullWidth
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={rejectMutation.isPending || !rejectionReason}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Application Dialog */}
      <Dialog open={newApplicationOpen} onClose={handleCloseNewApplication} maxWidth="md" fullWidth>
        <DialogTitle>New Loan Application</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 2 }}>
            {/* Customer Selection */}
            <Autocomplete
              options={customersData?.customers || []}
              getOptionLabel={(option) => `${option.fullName} (${option.idNumber})`}
              value={customersData?.customers?.find((c) => c.customerId === selectedCustomerId) || null}
              onChange={(_, newValue) => setSelectedCustomerId(newValue?.customerId || '')}
              inputValue={customerSearch}
              onInputChange={(_, newInputValue) => setCustomerSearch(newInputValue)}
              renderInput={(params) => (
                <TextField {...params} label="Customer *" placeholder="Search by name or ID" />
              )}
              loading={!customersData}
            />

            {/* Customer Details */}
            {selectedCustomer && (
              <Paper sx={{ p: 2, bgcolor: 'info.50', border: 1, borderColor: 'info.200' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="info.main">
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name:</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedCustomer.fullName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">ID Number:</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedCustomer.idNumber}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone:</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedCustomer.phoneNumber}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Credit Score:</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedCustomer.creditScore}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Current Loan Limit:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(
                        typeof selectedCustomer.currentLoanLimit === 'number'
                          ? selectedCustomer.currentLoanLimit
                          : parseFloat(selectedCustomer.currentLoanLimit || '0')
                      )}
                    </Typography>
                  </Box>
                  {customerLoanLimit && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Available Limit:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatCurrency(customerLoanLimit.availableLimit)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Product Selection */}
            <FormControl fullWidth required>
              <InputLabel>Loan Product</InputLabel>
              <Select
                value={selectedProductId}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setCalculation(null);
                }}
                label="Loan Product"
              >
                {products?.map((product: LoanProduct) => (
                  <MenuItem key={product.productId} value={product.productId}>
                    {product.productName} ({formatCurrency(product.minAmount)} - {formatCurrency(product.maxAmount)})
                    {product.processingFeeUpfront ? ' — Fee upfront' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Upfront fee notice */}
            {selectedProduct?.processingFeeUpfront && (
              <Alert severity="info">
                This product requires the customer to pay the processing fee via M-Pesa before the
                application enters the review queue. An STK push will be sent automatically on
                submission.
              </Alert>
            )}

            {/* Product Details */}
            {selectedProduct && (
              <Paper sx={{ p: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.main">
                  Product Details
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Product Name:</Typography>
                    <Typography variant="body2" fontWeight="medium">{selectedProduct.productName}</Typography>
                  </Box>
                  {selectedProduct.description && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">Description:</Typography>
                      <Typography variant="body2">{selectedProduct.description}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Interest Rate (1 Week):</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {selectedProduct.interestRate1Week}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Interest Rate (2 Weeks):</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {selectedProduct.interestRate2Weeks}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Interest Rate (1 Month):</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {selectedProduct.interestRate1Month}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Processing Fee:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedProduct.processingFeePercentage}%
                      {selectedProduct.processingFeeUpfront ? ' (paid upfront via M-Pesa)' : ' (deducted from disbursement)'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Penalty Rate:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {selectedProduct.latePaymentPenaltyPercentage}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Loan Range:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(selectedProduct.minAmount)} - {formatCurrency(selectedProduct.maxAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Requested Amount and Tenor */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                required
                label="Requested Amount"
                type="number"
                value={requestedAmount}
                onChange={(e) => {
                  setRequestedAmount(e.target.value);
                  setCalculation(null);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                helperText={
                  selectedProductId && products
                    ? `Min: ${formatCurrency(
                        products.find((p: LoanProduct) => p.productId === selectedProductId)?.minAmount || 0
                      )}, Max: ${formatCurrency(
                        products.find((p: LoanProduct) => p.productId === selectedProductId)?.maxAmount || 0
                      )}`
                    : ''
                }
              />
              <FormControl fullWidth required>
                <InputLabel>Tenor (Weeks)</InputLabel>
                <Select
                  value={tenorWeeks}
                  onChange={(e) => {
                    setTenorWeeks(e.target.value as 1 | 2 | 4);
                    setCalculation(null);
                  }}
                  label="Tenor (Weeks)"
                >
                  <MenuItem value={1}>1 Week</MenuItem>
                  <MenuItem value={2}>2 Weeks</MenuItem>
                  <MenuItem value={4}>4 Weeks (1 Month)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Loan Purpose */}
            <TextField
              fullWidth
              required
              label="Loan Purpose"
              multiline
              rows={2}
              value={loanPurpose}
              onChange={(e) => setLoanPurpose(e.target.value)}
              placeholder="e.g., Business expansion, Stock purchase, Equipment"
            />

            {/* Calculate Button */}
            <Button
              variant="outlined"
              startIcon={<CalculateIcon />}
              onClick={handleCalculate}
              disabled={!selectedProductId || !requestedAmount || calculateMutation.isPending}
              fullWidth
            >
              {calculateMutation.isPending ? 'Calculating...' : 'Calculate Loan Terms'}
            </Button>

            {/* Calculation Results */}
            {calculation && (() => {
              const principal = parseFloat(calculation.principal);
              const interest = parseFloat(calculation.interestAmount);
              const fee = parseFloat(calculation.processingFee);
              const totalDue = parseFloat(calculation.totalRepaymentAmount);
              const interestRate = parseFloat(calculation.interestRate);
              const disbursement = parseFloat(calculation.disbursementAmount);
              const isUpfrontFee = selectedProduct?.processingFeeUpfront ?? false;
              const dueDate = calculation.dueDate
                ? new Date(calculation.dueDate)
                : (() => {
                    const d = new Date();
                    d.setDate(d.getDate() + calculation.tenorWeeks * 7);
                    return d;
                  })();

              return (
                <Paper sx={{ p: 3, bgcolor: 'primary.50', border: 2, borderColor: 'primary.200' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                    Loan Calculation Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Loan Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Principal Amount:</Typography>
                        <Typography variant="h6" fontWeight="bold">{formatCurrency(principal)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Interest Rate:</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">{interestRate}%</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Tenor:</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {calculation.tenorWeeks === 1 ? '1 Week' : calculation.tenorWeeks === 2 ? '2 Weeks' : '1 Month (4 Weeks)'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Due Date:</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {format(dueDate, 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Cost Breakdown
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Interest Amount:</Typography>
                        <Typography variant="body1" fontWeight="bold">{formatCurrency(interest)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Processing Fee {isUpfrontFee ? '(paid upfront)' : '(deducted)'}:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">{formatCurrency(fee)}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: 'success.100', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Amount Customer Receives:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.dark">
                        {formatCurrency(disbursement)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isUpfrontFee
                          ? '(Full principal — fee paid separately)'
                          : '(Principal minus processing fee)'}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'error.100', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Total to Repay:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="error.dark">
                        {formatCurrency(totalDue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (Principal + Interest)
                      </Typography>
                    </Box>
                  </Box>

                  {isUpfrontFee && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      The processing fee of <strong>{formatCurrency(fee)}</strong> will be collected
                      via M-Pesa before disbursement. The customer will receive the full{' '}
                      <strong>{formatCurrency(disbursement)}</strong>.
                    </Alert>
                  )}
                </Paper>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewApplication}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleSubmitNewApplication}
            disabled={
              !selectedCustomerId ||
              !selectedProductId ||
              !requestedAmount ||
              !loanPurpose ||
              submitMutation.isPending
            }
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Applications;
